import Button, { ButtonTypes } from '~/components/atoms/Button/Button'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/DynamicIcon'
import Image, { urlFor } from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { PageData } from '~/components/report/types'
import { ComponentTheme } from '~/components/themes'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import useReportSignUp from '~/hooks/useReportSignUp'
import { useZepic } from '~/hooks/useZepic'

import styles from './ReportSummary.module.css'

interface ReportGateProps extends PageData {
  isLoggedIn: boolean
}

const ReportSummary = ({
  url,
  leadImage,
  summary,
  pdfFile,
  isLoggedIn,
  title,
}: ReportGateProps) => {
  const { openReportSignUp } = useReportSignUp()
  const { pushEvent } = useMabPostHog()
  const { track: zepicTrack } = useZepic()

  const buttonIcon = isLoggedIn ? (
    <Icon type="Download" />
  ) : (
    <Icon type="Lock" stroke="#ffffff" width={20} height={20} />
  )

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()

    if (!pdfFile?.url) {
      console.error('PDF file URL not available')
      return
    }

    const link = document.createElement('a')
    link.href = pdfFile.url
    link.target = '_blank'

    if (leadImage) {
      const zepObj = {
        pageUrl: link.href,
        title: title,
        image: urlFor(leadImage.asset, 800),
      }
      zepicTrack('report_downloaded', zepObj)
    }

    pushEvent('PDF Report Downloaded', {
      interactionLocation: PushInteractionLocation.Reports,
      interactionType: PushInteractionType.Click,
      webProduct: PushWebProduct.Reports,
    })

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const buttonText = isLoggedIn
    ? 'Download Report'
    : 'Unlock the report for free'
  const buttonClickHandler = (e: React.MouseEvent) =>
    isLoggedIn ? handleDownload(e) : openReportSignUp(leadImage)

  return (
    <Fixture verticalPadding={false} theme={ComponentTheme.secondary}>
      <div className={styles.base}>
        <div className={styles.media}>
          {leadImage && <Image asset={leadImage.asset} />}
        </div>
        <div className={styles.text}>
          <div className={styles.textInner}>
            <Text Tag="h3" size={TextSizes.primaryTitleMed}>
              About this report
            </Text>
            <Text Tag="p" size={TextSizes.magPrimaryTitleSml}>
              {summary}
            </Text>
            <div className={styles.cta}>
              <Button
                iconLeft={buttonIcon}
                button={{
                  onClick: buttonClickHandler,
                  type: ButtonTypes.button,
                }}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Fixture>
  )
}

export default ReportSummary
